import { StaticImage } from 'gatsby-plugin-image';
import type { FC } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AddOnsTemplate } from '@/components/add-ons/template';
import { GalleriesItem } from '@/components/galleries/item';
import { GalleriesList } from '@/components/galleries/list';
import { ComponentsHead } from '@/components/head';
import { AnchorWithTitle } from '@/components/links/anchor';
import { LinkWithTitle } from '@/components/links';

export const SOUTH_TAMPA_TITLE = 'South Tampa';

export const Head = () => {
  return (
    <ComponentsHead
      title="Explore South Tampa by Boat | Charter from TBBA"
      description="Glide along South Tampa’s waterfront with luxury boat charters. Cruise past Ballast Point, Westshore homes, and peaceful bayside scenery."
      keywords="South Tampa, Ballast Point, Luxury Charter, Waterfront Views"
    />
  );
};
const Page: FC = () => {
  return (
    <AddOnsTemplate
      title="South Tampa Boat Charters – Bayfront Luxury from Westshore to Ballast Point"
      subtitle=""
    >
      <GalleriesList>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${SOUTH_TAMPA_TITLE} header`}
              src="../../images/areas/south-tampa/0.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${SOUTH_TAMPA_TITLE} slide 1`}
              src="../../images/areas/south-tampa/1.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${SOUTH_TAMPA_TITLE} slide 1`}
              src="../../images/areas/south-tampa/2.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
      </GalleriesList>
      <p>
        South Tampa offers a scenic mix of upscale
        neighborhoods, beautiful bayfront homes, and serene
        waterways—all of which make it a prime destination
        for one of our private boat charters. This part of
        the city combines old Florida charm with modern
        luxury, and the best way to see it all is from the
        deck of a boat. Our South Tampa routes cruise along
        the eastern edge of the peninsula, offering calm
        waters, stunning homes, and peaceful vibes perfect
        for relaxing days on the water.
      </p>
      <p>
        Whether you’re celebrating something special or
        simply want a break from the city, South Tampa’s
        shoreline is full of eye-catching sights. We’ll pass
        by areas like{' '}
        <AnchorWithTitle href="https://www.tampa.gov/parks-and-recreation/programs/parks-and-facilities/greenways-and-trails/projects-under-development/bayshore-boulevard-greenway">
          Bayshore Boulevard
        </AnchorWithTitle>{' '}
        (home to one of the longest continuous sidewalks in
        the world), Ballast Point, and Westshore’s
        waterfront estates. This route is all about elegance
        and ease.
      </p>
      <h3>Available Charters in South Tampa:</h3>
      <ul>
        <li>
          
          <LinkWithTitle to="/services/8-hour-private-party-boat-charter/">
            8-Hour Private Party Boat
          </LinkWithTitle>
          : Your full-day escape begins here. With eight
          hours to spare, we can explore the bay, find
          secluded swim spots, and soak in everything the
          South Tampa shoreline has to offer.
        </li>
        <li>
          <LinkWithTitle to="/services/6-hour-private-party-boat-charter">
            6-Hour Private Party Boat
          </LinkWithTitle>
          : A fantastic mid-length charter that gives you
          time to cruise, swim, relax, and take in the
          sights without rushing. Great for an all-around
          experience.
        </li>
        <li>
          <LinkWithTitle to="/services/4-hour-private-party-boat-charter/">
            4-Hour Private Party Boat
          </LinkWithTitle>
          : A half-day on the water is just enough to
          discover South Tampa’s charm. Ideal for
          sightseeing and short swimming breaks.
        </li>
        <li>
          <LinkWithTitle to="/services/3-hour-wave-rave-djd-nightime-party-boat-charter/">
            3-Hour Sunset Charter
          </LinkWithTitle>
          : Watch the sun go down behind the luxurious homes
          of South Tampa while enjoying golden hour
          reflections on the bay. Pure serenity.
        </li>
        <li>
          <LinkWithTitle to="/services/3-hour-wave-rave-djd-nightime-party-boat-charter/">
            3-Hour Wave Rave Charter
          </LinkWithTitle>
          : South Tampa offers a unique backdrop for this
          high-energy cruise. Play your favorite music,
          toast to the views, and make it a moment.
        </li>
        <li>
          <LinkWithTitle to="/services/private-snorkeling-charters-st-petersburg/">
            Snorkeling Charters
          </LinkWithTitle>
          : We’ll head toward the clearer water for a
          snorkeling session, where you can cool off and see
          a different side of Tampa Bay’s natural beauty.
        </li>
        <li>
          <LinkWithTitle to="/services/large-group-charter-boat/">
            Large Group Boat Charter
          </LinkWithTitle>
          : South Tampa is perfect for hosting a larger
          gathering. Our luxury vessels can accommodate up
          to 30 guests, making it easy to bring everyone
          along for the ride.
        </li>
      </ul>
      <p>
        From the polished homes of Westshore to the
        palm-lined serenity of Ballast Point, South Tampa is
        a feast for the eyes. Hop aboard with Tampa Bay
        Boating Adventures and experience this beautiful
        corner of the bay in comfort and style.
      </p>
    </AddOnsTemplate>
  );
};

export default Page;
